// Copy + small formatting helpers shared by the report regenerate / revert / restore
// affordances. Centralizing this keeps the three actions' wording in lockstep — the modal
// body, the confirm button, and the toasts all read from one place — instead of being
// hand-duplicated across the modal and action components.

export type ReportActionKind = 'regenerate' | 'revert-previous' | 'restore-latest'

export interface ReportActionCopy {
  // GenericModal title.
  modalTitle: string
  // Fills "This {leadVerbPhrase} the cached report for X in <scope>."
  leadVerbPhrase: string
  // Lowercase gerund for the section-only caveat ("Note: {gerund} a Course Section …").
  gerund: string
  // Lowercase verb for the include-children checkbox + error toast ("Also {verb} all child …").
  verb: string
  // Verb for the include-children caption ("(slower; {childrenCaptionVerb} everything …)").
  childrenCaptionVerb: string
  // Confirm button label.
  confirmText: string
  // Info toast shown when the task is accepted.
  startedTitle: string
  // Success toast subtitle once the task completes.
  doneSubtitle: string
  // Error toast title when the task fails.
  failedTitle: string
}

export const REPORT_ACTION_COPY: Record<ReportActionKind, ReportActionCopy> = {
  regenerate: {
    modalTitle: 'Regenerate Report',
    leadVerbPhrase: 'clears and rebuilds',
    gerund: 'regenerating',
    verb: 'regenerate',
    childrenCaptionVerb: 'rebuilds',
    confirmText: 'Regenerate',
    startedTitle: 'Report regeneration started',
    doneSubtitle: 'The regenerated report is ready.',
    failedTitle: 'Report regeneration failed'
  },
  'revert-previous': {
    modalTitle: 'Revert Report',
    leadVerbPhrase: 'reverts to the previous version of',
    gerund: 'reverting',
    verb: 'revert',
    childrenCaptionVerb: 'reverts',
    confirmText: 'Revert',
    startedTitle: 'Report revert started',
    doneSubtitle: 'The reverted report is ready.',
    failedTitle: 'Report revert failed'
  },
  'restore-latest': {
    modalTitle: 'Restore Latest Report',
    leadVerbPhrase: 'restores the latest version of',
    gerund: 'restoring',
    verb: 'restore',
    childrenCaptionVerb: 'restores',
    confirmText: 'Restore latest',
    startedTitle: 'Report restore started',
    doneSubtitle: 'The latest report is ready.',
    failedTitle: 'Report restore failed'
  }
}

// "Fall 2026 Course Evals · Fall 2026" — the survey project and term that name the action
// target. Either part may be absent (older payloads), so empties are dropped.
export const formatReportScope = (surveyProjectTitle?: string, termName?: string): string =>
  [surveyProjectTitle, termName].filter(Boolean).join(' · ')

// Subtitle for the "started" toast: prefer a non-empty backend message, else name the scope
// echoed in the 202 response (e.g. "Reverting Fall 2026 Course Evals · Fall 2026. …"), else a
// generic line. `gerund` is the action's lowercase gerund; it is capitalized here.
export const buildStartedSubtitle = (message: string | undefined, gerund: string, echoedScope: string): string => {
  if (message) return message
  if (!echoedScope) return "It'll refresh automatically in a few minutes."
  const capitalizedGerund = gerund.charAt(0).toUpperCase() + gerund.slice(1)
  return `${capitalizedGerund} ${echoedScope}. It'll refresh automatically in a few minutes.`
}
